// Tab bar with an accent indicator that slides to the selected tab.

import { useLayoutEffect, useRef, useState } from 'preact/hooks';

export interface TabItem<T extends string> {
  id: T;
  label: string;
}

export interface TabsProps<T extends string> {
  items: TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  orientation?: 'horizontal' | 'vertical';
  label: string;
  class?: string;
}

export function Tabs<T extends string>({ items, value, onChange, orientation = 'horizontal', label, class: cls }: TabsProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ pos: number; size: number } | null>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const tab = list.querySelector<HTMLElement>(`[data-tab="${value}"]`);
      if (!tab) return setIndicator(null);
      setIndicator(orientation === 'horizontal' ? { pos: tab.offsetLeft, size: tab.offsetWidth } : { pos: tab.offsetTop, size: tab.offsetHeight });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(list);
    return () => ro.disconnect();
  }, [value, orientation, items.length]);

  const style = indicator
    ? orientation === 'horizontal'
      ? { transform: `translateX(${indicator.pos}px)`, width: `${indicator.size}px` }
      : { transform: `translateY(${indicator.pos}px)`, height: `${indicator.size}px` }
    : { opacity: 0 };

  return (
    <div ref={listRef} class={`tabs tabs-${orientation}${cls ? ` ${cls}` : ''}`} role="tablist" aria-label={label} aria-orientation={orientation}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          data-tab={item.id}
          class={`tab${item.id === value ? ' active' : ''}`}
          aria-selected={item.id === value}
          onClick={() => onChange(item.id)}
        >
          {item.label}
        </button>
      ))}
      <span class="tab-indicator" style={style} aria-hidden="true" />
    </div>
  );
}
